import { type AdminCompanyDetailDto, type AdminCompanyUserDto, CompanyType, UserCompanyRole } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { Avatar } from '@components/shared/avatar.component';
import { ConfirmDeleteModal } from '@components/shared/confirm-delete-modal.component';
import { EmptyStateContainer } from '@components/shared/empty-state-container.component';
import { OrganizationRoleSelect } from '@components/shared/organization-role-select.component';
import { ApiErrorWrapper } from '@hooks/api.hook';
import { errorToast, successToast } from '@hooks/toast.hook';
import {
  useAdminRemoveOrganizationMember,
  useAdminUpdateOrganizationMemberRole,
} from '@queries/intel-organization.query';
import { routes } from '@routes';
import { ArrowRightIcon } from 'lucide-react';
import { type FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminOrganizationMembersTile } from './admin-organization-members-tile.component';

type AdminOrganizationUsersTileProps = {
  organization: AdminCompanyDetailDto;
};

// Non-Intelligence orgs reuse the Members-tile layout, but with the generic company role
// (Admin/Collaborator) instead of a persona — and intentionally NO "add member" affordance
// (those orgs grow via in-org invites, not Platform-Admin adds).
export const AdminOrganizationUsersTile: FC<AdminOrganizationUsersTileProps> = (props) => {
  const { organization } = props;

  if (organization.type === CompanyType.INTELLIGENCE) {
    return <AdminOrganizationMembersTile organization={organization} />;
  }

  return <AdminOrganizationRegularUsersTile organization={organization} />;
};

const AdminOrganizationRegularUsersTile: FC<AdminOrganizationUsersTileProps> = (props) => {
  const { organization } = props;

  const { mutateAsync: updateRole } = useAdminUpdateOrganizationMemberRole();
  const { mutateAsync: removeMember } = useAdminRemoveOrganizationMember();

  const [memberToRemove, setMemberToRemove] = useState<AdminCompanyUserDto | null>(null);
  const [updatingMemberId, setUpdatingMemberId] = useState<string | null>(null);

  const users = organization.users ?? [];
  const isDeleted = Boolean(organization.deleted);

  const handleRoleChange = async (user: AdminCompanyUserDto, role: UserCompanyRole): Promise<void> => {
    if (role === user.role) return;

    setUpdatingMemberId(user.userCompanyId);
    try {
      await updateRole({ companyId: organization.id, data: { role }, userCompanyId: user.userCompanyId });
      successToast({ description: 'Member role updated.' });
    } catch (error) {
      const message =
        error instanceof ApiErrorWrapper ? error.message : 'Unable to update role. Please try again later.';
      errorToast({ description: message });
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleRemoveConfirm = async (): Promise<void> => {
    if (!memberToRemove) return;
    await removeMember({ companyId: organization.id, userCompanyId: memberToRemove.userCompanyId });
    setMemberToRemove(null);
  };

  if (users.length === 0) {
    return (
      <div className="flex min-h-0 flex-col rounded-lg border border-gray-200 bg-white p-4">
        <EmptyStateContainer title="No Users" />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-col rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900">Users ({users.length})</h3>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
        {users.map((user) => {
          const name = user.fullName || user.email;

          return (
            <div
              key={user.userCompanyId}
              className="group/member flex flex-col gap-3 rounded-lg border border-gray-100 p-4"
            >
              <div className="flex items-start gap-3">
                <Avatar
                  altText={name}
                  className="shrink-0"
                  imageUrl={user.avatarUrl ?? undefined}
                  initialText={name}
                  size="sm"
                />
                <div className="flex min-w-0 flex-1 flex-col pt-1">
                  <span className="break-words text-sm font-medium text-gray-900">{name}</span>
                  <span className="break-all text-xs text-gray-500">{user.email}</span>
                </div>
                <Link
                  className="shrink-0"
                  to={`${routes.adminUsers.path}?userId=${user.id}`}
                >
                  <IconButton
                    ariaLabel="View user in Admin Users"
                    className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover/member:opacity-100"
                    icon={<ArrowRightIcon />}
                    size="md"
                    title="View in Admin Users"
                    variant="secondary"
                  />
                </Link>
              </div>

              <OrganizationRoleSelect
                className="w-full"
                disabled={isDeleted || updatingMemberId === user.userCompanyId}
                onRemove={isDeleted ? undefined : () => setMemberToRemove(user)}
                onRoleChange={(role) => void handleRoleChange(user, role)}
                role={user.role}
              />
            </div>
          );
        })}
      </div>

      <ConfirmDeleteModal
        isOpen={Boolean(memberToRemove)}
        itemName={`${memberToRemove?.fullName || memberToRemove?.email || 'member'} from this organization`}
        onConfirm={handleRemoveConfirm}
        setIsOpen={(open) => {
          if (!open) setMemberToRemove(null);
        }}
      />
    </div>
  );
};
